import type { Scene } from "@babylonjs/core/scene";
import { WebXRDefaultExperience } from "@babylonjs/core/XR/webXRDefaultExperience";
import { WebXRState } from "@babylonjs/core/XR/webXRTypes";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import "@babylonjs/core/Helpers/sceneHelpers";
import { Rig } from "./rig";
import { SeedPouch, type Planter } from "./planting";
import { setupDesktopControls } from "./desktop";
import { setupControllers } from "./controllers";
import type { Terrain } from "../render/terrain";
import type { SimClient } from "../worker/client";
import { SPECIES, speciesIndex } from "../sim/species";
import { TextPanel } from "../render/panel";

/**
 * Wire up locomotion + planting over the terrain. Planting sends a seed to the
 * sim worker, which owns growth; the GardenRenderer draws the result.
 *
 * INVARIANT: `xr/rig.ts` is the ONLY module that writes the camera/rig
 * transform, and only under a full-opacity blink. `npm run xr:audit` enforces it.
 */
export function setupWorld(
  scene: Scene,
  canvas: HTMLCanvasElement,
  terrain: Terrain,
  sim: SimClient,
  start: { x: number; z: number },
): Rig {
  const rig = Rig.create(scene, canvas, terrain, start);
  const pouch = new SeedPouch();
  const planter: Planter = { plant: (id, x, z) => sim.plant(speciesIndex(id), x, z) };

  // Onboarding board at the start spot, facing the opening view.
  makeWelcomeBoard(scene, terrain, pouch, start);

  // Desktop dev path — always available, and the fallback when there's no XR.
  const desktop = setupDesktopControls(scene, rig, terrain, pouch, planter);

  if (import.meta.env.DEV) {
    (globalThis as unknown as { __garden: unknown }).__garden = { rig, pouch, planter, sim, terrain };
  }

  // Initialise WebXR in the background; never block first render on it.
  void WebXRDefaultExperience.CreateAsync(scene, {
    floorMeshes: [terrain.mesh],
    // Teleport + aiming are hand-rolled (blink fade, our own laser + reticle) for
    // exact comfort timings and a single, unambiguous target:
    disableTeleportation: true,
    disablePointerSelection: true, // else Babylon draws its OWN laser + ring cursor → a second target
    disableNearInteraction: true, // not used; avoids extra per-frame work
    disableHandTracking: true, // auto-enabled otherwise; loads rigged hand-mesh GLBs → same loader error
    // The tree-shaken build registers no glTF loader, so loading the controller
    // model GLBs throws "no plugin or fallback". We render our own laser/markers,
    // so skip the model load entirely (removes the error and a CDN dependency).
    inputOptions: { doNotLoadControllerMeshes: true },
  })
    .then((xr) => {
      rig.attachXR(xr);
      // Left-handed players can swap the aiming hand with ?hand=left in the URL.
      const dominantHand = new URLSearchParams(window.location.search).get("hand") === "left" ? "left" : "right";
      setupControllers(scene, xr, rig, terrain, pouch, planter, dominantHand);
      // The desktop reticle is driven by DOM pointer events, which stop firing
      // inside an immersive session — suspend it so it doesn't leave a stray disc
      // frozen at the spawn point.
      xr.baseExperience.onStateChangedObservable.add((state) => {
        if (state === WebXRState.IN_XR) desktop.suspend();
        else if (state === WebXRState.NOT_IN_XR) desktop.resume();
      });
    })
    .catch((err) => console.warn("WebXR unavailable; running flatscreen fallback.", err));

  return rig;
}

/**
 * A world-anchored board at the spawn point explaining the controls, visible in
 * both VR and desktop. The opening camera looks out along +Z, so the board sits
 * ahead of the player and faces back toward them. Its seed line tracks the pouch.
 */
function makeWelcomeBoard(scene: Scene, terrain: Terrain, pouch: SeedPouch, start: { x: number; z: number }): void {
  const board = new TextPanel(scene, "welcome", 2.4, 1.5);
  const y = terrain.heightAt(start.x, start.z) + 1.6;
  board.mesh.position.set(start.x, y, start.z + 4);
  board.mesh.rotation.set(0, 0, 0); // plane's readable face (-Z) already faces the player at spawn
  const render = (id: string): void => {
    const [r, g, b] = SPECIES[id as keyof typeof SPECIES].color;
    const col = `rgb(${(r * 255) | 0},${(g * 255) | 0},${(b * 255) | 0})`;
    board.setText(
      [
        { text: "Living Garden", fontPx: 60, color: "#f6eeda", align: "center" },
        { text: "You're standing on a living island — everything grows from a real ecology sim.", fontPx: 30, color: "#cdc3ac", align: "center", gapBefore: 8 },
        { text: "In VR", fontPx: 34, color: "#8fd8a0", gapBefore: 26 },
        { text: "Trigger — teleport where you point     Grip — plant a seed     Thumbstick — change seed", fontPx: 26, color: "#e8dfcc" },
        { text: "On desktop", fontPx: 34, color: "#8fd8a0", gapBefore: 16 },
        { text: "Click — teleport     Shift+Click — plant     1–4 or Q·E — change seed", fontPx: 26, color: "#e8dfcc" },
        { text: `Selected seed:  ● ${id}`, fontPx: 34, color: col, gapBefore: 24 },
      ],
      { bg: "rgba(16,13,9,0.86)", pad: 40 },
    );
  };
  const unsubscribe = pouch.onChange(render);

  // Onboarding only: dismiss the board once the player teleports away from spawn
  // (so it never shows its mirrored back as you explore). Reading camera position
  // is fine — the audit only guards camera *writes*.
  const spawn = new Vector3(start.x, y, start.z);
  const observer = scene.onBeforeRenderObservable.add(() => {
    const head = scene.activeCamera;
    if (!head) return;
    const p = head.globalPosition;
    if (Math.hypot(p.x - spawn.x, p.z - spawn.z) > 5) {
      unsubscribe(); // stop the pouch calling render on the disposed panel
      board.dispose();
      scene.onBeforeRenderObservable.remove(observer);
    }
  });
}

export { Rig };
